// Findings triage, as the backend runs it: verdicts on review findings and the
// tenant's escalation standards' answers, read through the analytics graph,
// and the standards, re-apply runs and consent managed through
// ConversationReviewService. Nothing here calls a model; the verifier runs in
// the trace-analysis pipeline for every tenant an operator has not turned off.
//
// Verdicts and answers come out in the shapes the Triage tab already draws
// (`verdicts[findingId]`, `answers[digest][findingId]`), so the list reads the
// server's results the way it read the browser's.
import { runQueryRows, rangeToTimeRange } from "./analyticsClient.js";
import { restGet, restPost, idForPath } from "./restClient.js";
import { bytesBase64ToUuid, enumSuffix, pick, uuidToBytesBase64 } from "./reviewClient.js";
import { CLAIM_KINDS, verdictKey } from "./triageLite.js";
import { parseServerTimestamp } from "./traceUtils.js";

// Finding ids per analytics read: the IN list is a bound parameter, and a few
// hundred keeps each read one index probe per granule set.
export const TRIAGE_ID_CHUNK = 500;
// Rows per page: the analytics API's record bound for both triage entities
// (their Discovery access pattern), which refuses a larger limit outright. A
// full page is read past, never trusted as the whole answer.
export const TRIAGE_ROWS = 1000;
// Pages per chunk before a read gives up rather than walk an unbounded tail.
const MAX_PAGES = 20;
// The graph's own window ceiling.
const MAX_WINDOW_MS = 90 * 86400 * 1000;
// A verification lands after its review; read that far past the pool.
const VERIFY_TAIL_MS = 7 * 86400 * 1000;
// A run's scope, as the API bounds it.
export const MAX_RUN_SCOPE = 500;
// The most findings one run may select, as the API and the worker bound it.
export const MAX_RUN_FINDINGS = 10_000;
// The longest window a run across the whole organization may cover; a run
// limited to sessions may cover the full 90 days.
export const MAX_TENANT_WIDE_RUN_DAYS = 7;
// Only quotes the verifier checked against what it read count as evidence.
const CHECKED = /^(ok|ref_corrected)/;

const V = (name) => ({ stepAlias: "v", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_FINDING_VERIFICATION_${name}` });
const A = (name) => ({ stepAlias: "a", field: `DIMENSION_FIELD_CONVERSATION_REVIEW_STANDARD_ANSWER_${name}` });
const inValues = (field, values) => ({ pred: { field, op: "OPERATOR_IN", value: values } });
const and = (children) => (children.length === 1 ? children[0] : { andExpr: { children } });
const eq = (field, value) => ({ pred: { field, op: "OPERATOR_EQ", value } });

const VERIFICATION_FIELDS = [
    ["FINDING_ID", "finding_id"], ["VERIFICATION_NODE_EXECUTION_ID", "execution_id"],
    ["VERIFIED_AT", "verified_at"], ["VERIFICATION_STATUS", "status"], ["STATUS_REASON", "status_reason"],
    ["LABEL", "label"], ["FAILED_CLAIM", "failed_claim"],
    ...CLAIM_KINDS.map((kind) => [`CLAIM_${kind.toUpperCase()}`, `claim_${kind}`]),
    ["COVERAGE", "coverage"], ["MODEL_CONFIGURATION_ID", "model"], ["COST_MICROS", "cost_micros"],
    ["STANDARD_INPUT_DIGEST", "input_digest"], ["EVIDENCE_CHECK_STATUS", "evidence_check_status"],
];
// Model-written from session content: read only for a role that may see it.
const VERIFICATION_TEXT = [["WHY", "why"], ["CORRECTED_CLAIM", "corrected_claim"], ["EVIDENCE_QUOTE", "evidence_quote"]];
// Where each quote was read from. Newer than the rest of the entity: an
// api-server deployed before it refuses the field, so the read asks without it
// once refused (see fetchVerdicts).
const EVIDENCE_MESSAGE = ["EVIDENCE_MESSAGE_ID", "evidence_message_id"];

const ANSWER_FIELDS = [
    ["FINDING_ID", "finding_id"], ["VERIFICATION_NODE_EXECUTION_ID", "execution_id"],
    ["STANDARD_INPUT_DIGEST", "input_digest"], ["STANDARD_ID", "standard_id"],
    ["STANDARD_VERSION", "standard_version"], ["STANDARD_VERSION_DIGEST", "digest"],
    ["OUTCOME", "outcome"], ["ASKED_AT", "asked_at"], ["MODEL_CONFIGURATION_ID", "model"],
    ["COST_MICROS", "cost_micros"],
];
const ANSWER_TEXT = [["WHY", "why"]];

// The window verifications of findings reviewed in [fromMs, toMs] fall in,
// ending at the moment of the read so verifications that land while the lens is
// open are read on the next fetch. Over the graph's ceiling the tail is cut,
// not the head: the oldest findings' verifications are the ones that exist.
export function verificationWindow({ fromMs, toMs }, nowMs = Date.now()) {
    const to = Math.min(Math.max(toMs, fromMs) + VERIFY_TAIL_MS, nowMs + 60_000);
    return { fromMs, toMs: Math.min(to, fromMs + MAX_WINDOW_MS) };
}

// The window a check across the whole organization asks. The API covers
// MAX_TENANT_WIDE_RUN_DAYS of verifications at once, so a longer one checks the
// newest week of the review window: the verification tail goes first (so "the
// last 7 days" fits), then the oldest findings. `cut` says the start moved.
export function tenantWideWindow({ fromMs, toMs }, nowMs = Date.now()) {
    const week = MAX_TENANT_WIDE_RUN_DAYS * 86400 * 1000;
    const span = verificationWindow({ fromMs, toMs }, nowMs);
    const end = Math.min(span.toMs, Math.max(toMs, fromMs + week));
    const start = Math.max(fromMs, end - week);
    return { fromMs: start, toMs: end, cut: start > fromMs };
}

// Answers are timed by when they were asked, which a re-apply makes now, so the
// window runs to the moment of the read. A range older than the graph's
// ceiling reads the latest 90 days of answers to it.
export function answerWindow({ fromMs }, nowMs = Date.now()) {
    const to = nowMs + 60_000;
    return { fromMs: Math.max(fromMs, to - MAX_WINDOW_MS), toMs: to };
}

function spec({ entity, alias, fields, filter, window, order, offset = 0 }) {
    return {
        root: { alias, entity },
        timeRange: rangeToTimeRange({ from: new Date(window.fromMs).toISOString(), to: new Date(window.toMs).toISOString() }),
        filter,
        output: { dimensions: fields.map(([field, outputAlias]) => ({ field, outputAlias })) },
        orderBy: [{ outputAlias: order, direction: "DIRECTION_DESC" }],
        limit: TRIAGE_ROWS,
        ...(offset ? { offset } : {}),
    };
}

// Only completed verifications: an attempt that ended early (a limit, or a
// request the provider refused) says nothing about the finding, so a finding
// with no completed one reads as not yet verified.
export function verificationSpec({ ids, window, sensitive = true, offset = 0, messages = sensitive }) {
    const text = sensitive ? [...VERIFICATION_TEXT, ...(messages ? [EVIDENCE_MESSAGE] : [])] : [];
    const fields = [...VERIFICATION_FIELDS, ...text].map(([n, a]) => [V(n), a]);
    const filter = and([inValues(V("FINDING_ID"), ids), eq(V("VERIFICATION_STATUS"), "completed")]);
    return spec({ entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING_VERIFICATION", alias: "v", fields, filter, window, order: "verified_at", offset });
}

// What the standards question reads of a verification, under the worker's
// column names: the in-browser preview builds the worker's question from it.
// Title, category and rationale are sensitive like the verifier's own text, so
// only a role that may read session content can try a standard.
const QUESTION_FIELDS = [
    ["FINDING_ID", "finding_id"], ["VERIFICATION_NODE_EXECUTION_ID", "execution_id"], ["VERIFIED_AT", "verified_at"],
    ["VERIFICATION_STATUS", "status"], ["LABEL", "label"], ["FAILED_CLAIM", "failed_claim"],
    ["FINDING_SEVERITY", "finding_severity"], ["EVIDENCE_CLAIM", "evidence_claim"], ["EVIDENCE_CHECK_STATUS", "evidence_check_status"],
    ["FINDING_TITLE", "finding_title"], ["FINDING_CATEGORY", "finding_category"], ["FINDING_RATIONALE", "finding_rationale"],
    ["WHY", "why"], ["CORRECTED_CLAIM", "corrected_claim"], ["EVIDENCE_QUOTE", "evidence_quote"],
];
export function questionSpec({ ids, window, offset = 0 }) {
    const filter = and([inValues(V("FINDING_ID"), ids), eq(V("VERIFICATION_STATUS"), "completed")]);
    return spec({ entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING_VERIFICATION", alias: "v", fields: QUESTION_FIELDS.map(([n, a]) => [V(n), a]), filter, window, order: "verified_at", offset });
}

// `digests` narrows the read to the standard versions the tab can show; every
// version of every standard ever asked would otherwise fill the pages.
export function answerSpec({ ids, window, digests = null, sensitive = true, offset = 0 }) {
    const fields = [...ANSWER_FIELDS, ...(sensitive ? ANSWER_TEXT : [])].map(([n, a]) => [A(n), a]);
    const filter = and([inValues(A("FINDING_ID"), ids), ...(digests?.length ? [inValues(A("STANDARD_VERSION_DIGEST"), digests)] : [])]);
    return spec({ entity: "ENTITY_TYPE_CONVERSATION_REVIEW_STANDARD_ANSWER", alias: "a", fields, filter, window, order: "asked_at", offset });
}

const chunks = (ids) => {
    const unique = [...new Set((ids || []).filter(Boolean).map((id) => String(id).toLowerCase()))];
    const out = [];
    for (let i = 0; i < unique.length; i += TRIAGE_ID_CHUNK) out.push(unique.slice(i, i + TRIAGE_ID_CHUNK));
    return out;
};
// Every page of one chunk: a full page means there may be more.
async function readPages(build, opts) {
    const rows = [];
    for (let page = 0; page < MAX_PAGES; page += 1) {
        const got = (await runQueryRows(build(page * TRIAGE_ROWS), opts)) || [];
        rows.push(...got);
        if (got.length < TRIAGE_ROWS) return rows;
    }
    return rows;
}
const readAll = async (ids, build, { signal = null, fresh = false } = {}) => {
    const opts = { signal, ...(fresh ? { cache: false } : {}) };
    return (await Promise.all(chunks(ids).map((part) => readPages((offset) => build(part, offset), opts)))).flat().filter(Boolean);
};
const str = (v) => (v == null ? "" : String(v));
const arr = (v) => (Array.isArray(v) ? v.map(str) : []);
const usd = (micros) => (Number(micros) || 0) / 1e6;

// The quotes a verdict rests on: checked against what the verifier read and not
// empty. An item the check failed or never ran is not evidence.
// Each carries the session message it was read from, where the verifier
// matched one, so it can be placed on the session's turns.
function checkedEvidence(row) {
    const statuses = arr(row.evidence_check_status);
    const messages = Array.isArray(row.evidence_message_id) ? row.evidence_message_id : [];
    return arr(row.evidence_quote)
        .map((text, i) => ({ id: `q${i}`, text, status: statuses[i] || "", messageId: str(messages[i]).toLowerCase() || null }))
        .filter((e) => e.text.trim() && CHECKED.test(e.status))
        .map(({ id, text, messageId }) => ({ id, text, messageId }));
}

// One completed verification row as the verdict the list draws.
export function verdictFromRow(row) {
    return {
        findingId: str(row.finding_id),
        verdict: str(row.label),
        failedClaim: str(row.failed_claim),
        correctedClaim: str(row.corrected_claim),
        why: str(row.why),
        claims: CLAIM_KINDS.map((kind) => ({ kind, status: str(row[`claim_${kind}`]) })),
        evidence: checkedEvidence(row),
        // "none": the finding cited messages and the verifier found none of
        // them in what it read.
        citationLocated: row.coverage !== "none",
        model: str(row.model),
        costUsd: usd(row.cost_micros),
        verifiedAt: str(row.verified_at),
        executionId: str(row.execution_id),
        inputDigest: str(row.input_digest),
    };
}

// The verification each finding shows: its newest completed one. Rows arrive
// newest first, and the read asks only for completed ones.
export function currentVerdicts(rows) {
    const out = {};
    for (const row of rows || []) {
        const id = str(row.finding_id);
        if (!id || row.status !== "completed" || !row.label || out[id]) continue;
        out[id] = verdictFromRow(row);
    }
    return out;
}

// Answers as the list keys them: by the standard version's digest, then the
// finding, and only for the verification the finding shows. An answer to an
// earlier verification of the finding judged a different record.
export function answersFor(rows, verdicts) {
    const out = {};
    for (const row of rows || []) {
        const id = str(row.finding_id);
        const v = verdicts?.[id];
        if (!v || row.execution_id !== v.executionId || row.input_digest !== v.inputDigest) continue;
        const digest = str(row.digest);
        // Rows arrive newest first: a later evaluator's answer wins.
        if (out[digest]?.[id]) continue;
        out[digest] = {
            ...(out[digest] || {}),
            [id]: {
                key: verdictKey(v),
                escalates: row.outcome === "met",
                unconfirmed: row.outcome === "unconfirmed",
                withheld: false,
                failed: row.outcome === "error",
                standardWhy: str(row.why),
                model: str(row.model),
                at: str(row.asked_at),
                costUsd: usd(row.cost_micros),
            },
        };
    }
    return out;
}

// Harness seam (lanes-harness ?reviews=1&triage=lite): pre-seeded rows and
// standards answer the reads below in place of the backend, through the same
// shaping the backend's answers get.
let SEEDED = null;
// `delayMs` holds the verdict and answer reads back that long, as a slow
// backend would, to see what reads before them.
export function seedFindingsTriage({ verifications = [], answers = [], questions = [], standards = [], runs = [], statuses = [], start = null, delayMs = 0 } = {}) {
    SEEDED = { verifications, answers, questions, standards, runs, statuses, start, delayMs };
}
export function clearSeededFindingsTriage() {
    SEEDED = null;
}
const seededFor = (rows, ids) => {
    const wanted = new Set((ids || []).map((id) => String(id).toLowerCase()));
    return rows.filter((r) => wanted.has(String(r.finding_id).toLowerCase()));
};
const seededHeld = (out) => (SEEDED?.delayMs > 0 ? new Promise((resolve) => setTimeout(() => resolve(out), SEEDED.delayMs)) : out);

// When Triage first checked a finding, near a range: its earliest completed
// verification from a week before the range on, or null for none. The flow
// above the lens starts there when Triage started inside the range, so the
// findings from before it, which Triage was never going to check, are not
// counted as ones it has not checked.
const TRIAGE_START_LOOKBACK_MS = 7 * 86400 * 1000;
export function triageStartSpec({ fromMs }, nowMs = Date.now()) {
    const toMs = nowMs + 60_000;
    const from = Math.max(fromMs - TRIAGE_START_LOOKBACK_MS, toMs - MAX_WINDOW_MS);
    return {
        root: { alias: "v", entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING_VERIFICATION" },
        timeRange: rangeToTimeRange({ from: new Date(from).toISOString(), to: new Date(toMs).toISOString() }),
        filter: eq(V("VERIFICATION_STATUS"), "completed"),
        output: { dimensions: [{ field: V("VERIFIED_AT"), outputAlias: "verified_at" }] },
        orderBy: [{ outputAlias: "verified_at", direction: "DIRECTION_ASC" }],
        limit: 1,
    };
}
export async function fetchTriageStart({ fromMs, signal = null }) {
    if (SEEDED) return Number.isFinite(SEEDED.start) ? SEEDED.start : null;
    const rows = await runQueryRows(triageStartSpec({ fromMs }), { signal });
    const at = parseServerTimestamp(rows?.[0]?.verified_at);
    return Number.isFinite(at) ? at : null;
}

// `reviewWindow` is the lens's review-completion window; the read windows are
// derived from it at the moment of the read.
// Whether this api-server refused the evidence's message ids, so later reads
// stop asking for them. Per page load: a redeploy reloads the dashboard.
let messagesRefused = false;
export function _resetEvidenceMessagesForTests() { messagesRefused = false; }
// The api-server's refusal of the field itself, not any failed read: a 400
// naming it, or, from a backend compiled before the wire value existed, the
// Unspecified field it decodes it as.
function refusesEvidenceMessages(error) {
    if (error?.status !== 400) return false;
    const details = `${error?.responseText || ""}
${error?.message || ""}`;
    if (details.includes(V(EVIDENCE_MESSAGE[0]).field)) return true;
    return details.includes("field Unspecified does not belong to entity") && details.includes("(alias: v)");
}
export async function fetchVerdicts({ ids, reviewWindow, sensitive = true, signal = null, fresh = false }) {
    if (SEEDED) return seededHeld(currentVerdicts(seededFor(SEEDED.verifications, ids)));
    const window = verificationWindow(reviewWindow);
    const read = (messages) => readAll(ids, (part, offset) => verificationSpec({ ids: part, window, sensitive, offset, messages }), { signal, fresh });
    const messages = sensitive && !messagesRefused;
    try {
        return currentVerdicts(await read(messages));
    } catch (error) {
        if (!messages || signal?.aborted || !refusesEvidenceMessages(error)) throw error;
        // An api-server older than the field refuses the whole read; the
        // verdicts do not depend on it, so read them without.
        const rows = await read(false);
        messagesRefused = true;
        return currentVerdicts(rows);
    }
}

// The verification each finding shows, as the standards question reads it:
// its newest completed row, keyed by finding id.
export async function fetchQuestionRows({ ids, reviewWindow, signal = null }) {
    const window = verificationWindow(reviewWindow);
    const rows = SEEDED ? seededFor(SEEDED.questions, ids)
        : await readAll(ids, (part, offset) => questionSpec({ ids: part, window, offset }), { signal });
    const out = {};
    for (const row of rows) {
        const id = str(row.finding_id);
        if (id && row.status === "completed" && row.label && !out[id]) out[id] = row;
    }
    return out;
}

export async function fetchAnswers({ ids, reviewWindow, verdicts, digests = null, sensitive = true, signal = null, fresh = false }) {
    if (SEEDED) return seededHeld(answersFor(seededFor(SEEDED.answers, ids), verdicts));
    const window = answerWindow(reviewWindow);
    const rows = await readAll(ids, (part, offset) => answerSpec({ ids: part, window, digests, sensitive, offset }), { signal, fresh });
    return answersFor(rows, verdicts);
}

// Why a finding has no completed check, where the verifier recorded an
// attempt: one that ended early (`incomplete`, with the reason it gives) or
// was never run. Labels only: nothing here is session content.
export function checkStatusSpec({ ids, window, offset = 0 }) {
    const fields = [["FINDING_ID", "finding_id"], ["VERIFIED_AT", "verified_at"], ["VERIFICATION_STATUS", "status"], ["STATUS_REASON", "status_reason"]]
        .map(([n, a]) => [V(n), a]);
    const filter = and([inValues(V("FINDING_ID"), ids), inValues(V("VERIFICATION_STATUS"), ["incomplete", "not_run"])]);
    return spec({ entity: "ENTITY_TYPE_CONVERSATION_REVIEW_FINDING_VERIFICATION", alias: "v", fields, filter, window, order: "verified_at", offset });
}

// The newest such attempt per finding: { id: { status, reason, at } }.
export function checkStatusesFrom(rows) {
    const out = {};
    for (const row of rows || []) {
        const id = str(row.finding_id).toLowerCase();
        if (!id || out[id]) continue;
        out[id] = { status: str(row.status), reason: str(row.status_reason), at: str(row.verified_at) };
    }
    return out;
}

export async function fetchCheckStatus({ ids, reviewWindow, signal = null }) {
    if (SEEDED) return checkStatusesFrom(seededFor(SEEDED.statuses || [], ids));
    if (!ids?.length) return {};
    const window = verificationWindow(reviewWindow);
    return checkStatusesFrom(await readAll(ids, (part, offset) => checkStatusSpec({ ids: part, window, offset }), { signal }));
}

// -- ConversationReviewService ---------------------------------------------------

// Origin's own standards (shared_db::builtin_standards). The API lists them for
// every tenant so their answers count toward escalation, but they are never
// shown, edited, or re-applied here.
const BUILT_IN_STANDARD_IDS = new Set(["aicar-test-string"]);

// A standard as the Triage tab's library holds one. Its id is the standard's
// own; `digest` names the version answers are keyed by.
export function adaptStandard(s) {
    const id = str(pick(s, "standard_id", "standardId"));
    return {
        id,
        builtIn: BUILT_IN_STANDARD_IDS.has(id),
        digest: str(pick(s, "version_digest", "versionDigest")),
        standardId: str(pick(s, "standard_id", "standardId")),
        name: str(s?.name),
        version: Number(s?.version) || 1,
        text: str(s?.body),
        live: Boolean(pick(s, "live_for_new_findings", "liveForNewFindings")),
        createdAt: str(pick(s, "created_at", "createdAt")),
    };
}

export async function listStandards({ signal } = {}) {
    if (SEEDED) return SEEDED.standards.map(adaptStandard);
    const body = await restGet("/v1/escalation-standards", null, { signal });
    return (body?.standards || []).map(adaptStandard);
}

// A new standard's id, from its name: the table's id alphabet, unique enough
// that two names rarely meet.
export function standardIdFor(name) {
    const base = str(name).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 48) || "standard";
    const tail = Math.random().toString(36).slice(2, 8);
    return `${base}-${tail}`;
}

// `live` left out keeps a revision's setting as the server holds it.
export async function writeStandard({ standardId, expectedVersion, name, text, live }) {
    return adaptStandard(await restPost("/v1/escalation-standards:write", {
        standardId, expectedVersion: String(expectedVersion || 0), name, body: text,
        ...(live === undefined ? {} : { liveForNewFindings: Boolean(live) }),
    }));
}

export async function setStandardLive({ standardId, expectedVersion, live }) {
    return adaptStandard(await restPost("/v1/escalation-standards:set-live", {
        standardId, expectedVersion: String(expectedVersion), liveForNewFindings: Boolean(live),
    }));
}

export async function retireStandard({ standardId, expectedVersion }) {
    const body = await restPost("/v1/escalation-standards:retire", { standardId, expectedVersion: String(expectedVersion) });
    return Number(pick(body, "retired_version", "retiredVersion")) || 0;
}

const RUN_DONE = new Set(["published", "failed", "retired"]);
export function adaptRun(r) {
    const status = enumSuffix("STANDARD_APPLICATION_RUN_STATUS_", r?.status);
    const n = (snake, camel) => Number(pick(r, snake, camel)) || 0;
    return {
        runId: bytesBase64ToUuid(pick(r, "run_id", "runId")),
        standardId: str(pick(r, "standard_id", "standardId")),
        standardVersion: n("standard_version", "standardVersion"),
        digest: str(pick(r, "standard_version_digest", "standardVersionDigest")),
        status,
        done: RUN_DONE.has(status),
        selected: n("selected", "selected"),
        asked: n("asked", "asked"),
        met: n("met", "met"),
        notMet: n("not_met", "notMet"),
        unconfirmed: n("unconfirmed", "unconfirmed"),
        error: n("error", "error"),
        costUsd: usd(pick(r, "spend_micros", "spendMicros")),
        createdAt: str(pick(r, "created_at", "createdAt")),
        scope: (pick(r, "scope_trace_ids", "scopeTraceIds") || []).map(bytesBase64ToUuid).filter(Boolean),
    };
}

// A run's arguments on the wire: `scope` null is the whole tenant.
function runBody({ standardId, standardVersion, fromMs, toMs, scope }) {
    return {
        standardId,
        standardVersion: String(standardVersion),
        windowFrom: new Date(fromMs).toISOString(),
        windowTo: new Date(toMs).toISOString(),
        scopeTraceIds: (scope || []).slice(0, MAX_RUN_SCOPE).map(uuidToBytesBase64).filter(Boolean),
    };
}

export async function createRun(args) {
    return adaptRun(await restPost("/v1/standard-application-runs", runBody(args)));
}

// What the same run would do: how many findings it covers and how many it
// would ask (the rest already have an answer).
export async function previewRun(args, { signal } = {}) {
    const body = await restPost("/v1/standard-application-runs:preview", runBody(args), { signal });
    return {
        inScope: Number(pick(body, "in_scope", "inScope")) || 0,
        notYetAsked: Number(pick(body, "not_yet_asked", "notYetAsked")) || 0,
    };
}

export async function getRun(runId, { signal } = {}) {
    const b64 = uuidToBytesBase64(runId);
    if (!b64) throw new Error("run id is not a uuid");
    return adaptRun(await restGet(`/v1/standard-application-runs/${idForPath(b64)}`, null, { signal }));
}

export async function listRuns({ pageSize = 20, signal } = {}) {
    if (SEEDED) return SEEDED.runs.map(adaptRun);
    const body = await restGet("/v1/standard-application-runs", { page_size: pageSize }, { signal });
    return (body?.runs || []).map(adaptRun);
}

// The gateway's refusals, in the reader's words.
export function triageErrorText(error) {
    switch (error?.grpcCode) {
    case "ABORTED": return "Someone else changed this standard. Reload to see their version.";
    case "FAILED_PRECONDITION":
        return /retired/i.test(error.serverMessage || "")
            ? "This standard was removed, so it cannot be re-applied."
            : "Findings triage is off for this organization. An administrator can turn it on under Policy.";
    case "RESOURCE_EXHAUSTED": return error.serverMessage || "Too many standards or runs at once.";
    case "PERMISSION_DENIED": return "Your role cannot make this change.";
    default: return error?.serverMessage || "The request failed. Try again.";
    }
}
